// 언어 변경 드롭다운 — KRDS 「언어 변경(Language switcher)」 드롭다운 유형을 따른다.
// https://www.krds.go.kr/html/site/component/component_10_01.html
//
//   · 구조: [지구본 아이콘 + 레이블] 버튼 → 현재 언어 표시 → 옵션 목록
//   · 지원 언어가 3개(ko/ja/en)라 링크형이 아니라 드롭다운형을 쓴다
//   · 옵션은 '원어 - 현재 화면 언어로 쓴 이름' (예: 한국어 화면의 "日本語 - 일본어").
//     언어 코드(KO/JP)로 줄여 쓰지 않는다 — 그 언어를 읽는 사람이 자기 언어를 찾을 수 있어야 한다
//   · 원어 부분에는 lang 속성을 달아 스크린 리더가 그 언어의 발음으로 읽게 한다
//   · 키보드: ↓/↑/Home/End 로 이동, Enter·Space 로 선택, Esc 로 닫고 버튼으로 포커스 복귀
import React, { useEffect, useRef, useState } from 'react';
import { LANGS, LANG_NATIVE, LangId, setLang, useT } from '../../lib/locale';
import { MessageKey } from '../../lib/locale.messages';
import styles from './TopNav.module.css';

const NAME_KEY: Record<LangId, MessageKey> = {
  ko: 'lang.name.ko',
  ja: 'lang.name.ja',
  en: 'lang.name.en',
};

export default function LanguageSwitcher() {
  const { lang, t } = useT();
  const [open, setOpen] = useState(false);
  const [announce, setAnnounce] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  /* 바깥 클릭 · Esc 로 닫기 */
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        if (buttonRef.current) buttonRef.current.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /* 열릴 때 현재 언어 옵션에 포커스 */
  useEffect(() => {
    if (!open) return;
    const el = optionRefs.current[LANGS.indexOf(lang)];
    if (el) el.focus();
  }, [open, lang]);

  const focusOption = (index: number) => {
    const n = LANGS.length;
    const el = optionRefs.current[(index + n) % n];
    if (el) el.focus();
  };

  const onButtonKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const onListKeyDown = (e: React.KeyboardEvent) => {
    const current = optionRefs.current.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); focusOption(current + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); focusOption(current - 1); }
    else if (e.key === 'Home') { e.preventDefault(); focusOption(0); }
    else if (e.key === 'End') { e.preventDefault(); focusOption(LANGS.length - 1); }
    else if (e.key === 'Tab') setOpen(false);
  };

  const choose = (next: LangId) => {
    setOpen(false);
    if (buttonRef.current) buttonRef.current.focus();
    if (next === lang) return;
    setLang(next);
    // 안내 문구는 바뀐 언어로 읽힌다 — 선택한 사람이 이해할 수 있는 언어로 결과를 알린다
    setAnnounce(`${LANG_NATIVE[next]}`);
  };

  return (
    <div className={styles.langRoot} ref={rootRef}>
      <button
        type="button"
        ref={buttonRef}
        className={`${styles.langBtn} ${open ? styles.langBtnOpen : ''}`}
        onClick={() => setOpen(o => !o)}
        onKeyDown={onButtonKeyDown}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`${t('lang.button')} (Language) — ${LANG_NATIVE[lang]}`}
        title={t('lang.button')}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/>
          <path d="M2 12h20"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
        <span lang={lang}>{LANG_NATIVE[lang]}</span>
        <svg className={styles.langChevron} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
          style={{ transform: open ? 'rotate(180deg)' : 'none' }}>
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </button>

      {open && (
        <div className={styles.langMenu} role="group" aria-label={t('lang.listLabel')} onKeyDown={onListKeyDown}>
          <div className={styles.langMenuTitle}>{t('lang.listLabel')}</div>
          <ul className={styles.langList}>
            {LANGS.map((id, i) => {
              const selected = id === lang;
              return (
                <li key={id}>
                  <button
                    type="button"
                    ref={el => { optionRefs.current[i] = el; }}
                    className={`${styles.langOption} ${selected ? styles.langOptionOn : ''}`}
                    onClick={() => choose(id)}
                    aria-current={selected ? 'true' : undefined}
                  >
                    <span className={styles.langOptionText}>
                      <span lang={id} className={styles.langOptionNative}>{LANG_NATIVE[id]}</span>
                      {/* 현재 화면 언어와 같은 옵션은 원어만 쓴다 — '한국어 - 한국어' 처럼 겹치지 않게 */}
                      {!selected && <span className={styles.langOptionLocal}> - {t(NAME_KEY[id])}</span>}
                    </span>
                    {selected && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* 스크린 리더용 변경 알림 */}
      <span className={styles.srOnly} aria-live="polite" lang={lang}>
        {announce && t('lang.changed', { name: announce })}
      </span>
    </div>
  );
}
